import { Injectable } from '@angular/core';
import { firstValueFrom, from, Observable, of } from 'rxjs';
import { map, switchMap, take } from 'rxjs/operators';
import { ResponseDto } from 'src/app/models/dto/responseDto';
import { BaseTestItemDto } from 'src/app/models/dto/Base-Test-item-dto';
import { TestAnalysisDto } from 'src/app/models/dto/testAnalysisDto';
import { formField } from 'src/app/models/formField';
import { ApolloClientService } from './apollo-client.service';
import { GraphQLBaseTestItemOutput } from 'src/app/graphQL/models/output/graphql-base-test-item-output';
import { GETALL_TEST_ANALYSIS_QUERY, GETALL_TEST_ANALYSIS_QUERY_PAGINATED, GET_TEST_ANALYSIS_QUERY, GET_TEST_ANALYSIS_SEARCH_UI_TEMPLATE_QUERY, GET_TEST_ANALYSIS_UI_TEMPLATE_QUERY, SEARCH_TEST_ANALYSIS_QUERY } from 'src/app/graphQL/queries/test-analysis-queries';
import { convertGraphqlResponseToDto, convertGraphqlResponseToDtoSimpleReturnValue } from 'src/app/graphQL/mappers/common.mappers';
import { GraphQLResponseOutput } from 'src/app/graphQL/models/output/graphql-response-output';
import { GraphQLFormFieldOutput } from 'src/app/graphQL/models/output/graphql-form-field-output';
import { convertGraphqlFormFieldToDto, convertGraphqlBaseTestItemToDto } from 'src/app/graphQL/mappers/common.mappers';
import { CREATE_TEST_ANALYSIS_MUTATION, DELETE_TEST_ANALYSIS_MUTATION, UPDATE_TEST_ANALYSIS_MUTATION } from 'src/app/graphQL/mutations/test-analysis-mutations';
import { convertTestAnalysisDtoToGraphqlBaseTestItem } from 'src/app/graphQL/mappers/test-analysis.mappers';
import { GET_ITEMS_WITH_LINK_QUERY } from 'src/app/graphQL/queries/item-queries';
import { ItemTypeMapper } from '../mappers/itemTypeMapper';
import { ItemType } from 'src/app/models/enums/itemType';
import { environment } from 'src/environments/environment';
import { Guid } from 'src/app/constants/guidConstants';
import { GraphQLFieldTypeEnum } from 'src/app/graphQL/enums/graphql-field-type-enum';
import { DualApiService, Scope } from '../migration/dual-api.service';

@Injectable({
  providedIn: 'root'
})
export class TestAnalysisApiService {

  constructor(private apolloClientService: ApolloClientService, private dualApiService: DualApiService) { }

  getAllPaginatedPages(
    company: string,
    regBy: string,
    pageSize: number,
    functionalGroupId: string
  ): Observable<BaseTestItemDto[]> {
    if(environment.IsHero && !regBy)
      functionalGroupId = Guid.HERO;
      return this.apolloClientService.apolloClient$.pipe(
        take(1),
        switchMap(client => {
          let page = 1; // mutable state for clarity & correctness

          const fetchPage = (p: number) =>
            from(client.query<{ configuratorAllTestCasesPagination: GraphQLBaseTestItemOutput[] }>({
              query: GETALL_TEST_ANALYSIS_QUERY_PAGINATED,
              variables: {
                Company: company,
                RegBy: regBy,
                OrgGroup: functionalGroupId,
                Page: p,
                PageSize: pageSize
              },
              fetchPolicy: 'network-only' //ensure each page hits the network
            })).pipe(
              map(res => res.data?.configuratorAllTestCasesPagination ?? []),
              map(items => items.map(convertGraphqlBaseTestItemToDto)),
              // tap(items => console.log(`[pagination] page ${p} -> ${items.length}`))//for debug
            );

          // seed
          return fetchPage(page).pipe(
            // Recurse while we keep getting full pages
            // We'll re-emit by chaining promises via recursion using Rx:
            // Convert to a custom Observable that pulls subsequent pages synchronously
            // but emits each page as it arrives.
            switchMap(firstItems => new Observable<BaseTestItemDto[]>(subscriber => {
              // emit first page
              subscriber.next(firstItems);

              let lastPageLength = firstItems.length;

              const next = async () => {
                try {
                  // loop while we got a full page previously
                  while (lastPageLength === pageSize) {
                    page += 1;
                    const nextItems = await firstValueFrom(fetchPage(page));
                    subscriber.next(nextItems);
                    lastPageLength = nextItems.length; // update stop condition
                  }
                  subscriber.complete();
                } catch (err) {
                  subscriber.error(err);
                }
              };

              // kick off the loop
              next().catch(err => subscriber.error(err));
            }))
          );
        })
      );
  }

  getAll(company: string, regBy: string, orgGroupId?: string): Observable<BaseTestItemDto[]> {
    const oldApiCall$ = this.apolloClientService.apolloClient$.pipe(
      switchMap(client =>
        client.query<{configuratorAllTestAnalysis : GraphQLBaseTestItemOutput[]}>({
          query: GETALL_TEST_ANALYSIS_QUERY,
          variables: {Company: company, RegBy: regBy, OrgGroup: orgGroupId}
        })
      ),
      map(results => {
        const testAnalyses = results.data.configuratorAllTestAnalysis
        if (!testAnalyses)
          return undefined

        return testAnalyses.map(item => {
          return convertGraphqlBaseTestItemToDto(item);
        })
      })
    );

    let scope: Scope | undefined;
    if (regBy) scope = 'MINE';
    else if (orgGroupId) scope = 'MY_GROUP';

    return this.dualApiService.dualGetAll('testAnalyses', [], oldApiCall$, scope);
  }

  getByUid(uid: string): Observable<ResponseDto> {
    const oldApiCall$ = this.apolloClientService.apolloClient$.pipe(
      switchMap(client =>
        client.query<{configuratorTestAnalysisById: GraphQLResponseOutput}>({
          query: GET_TEST_ANALYSIS_QUERY,
          variables: {Uid: uid}
        })
      ),
      map(results => {
        const testAnalysis = results.data.configuratorTestAnalysisById
        if (!testAnalysis)
          return undefined

        return convertGraphqlResponseToDto<TestAnalysisDto>(testAnalysis);
      })
    );

    return this.dualApiService.dualGetByUid('testAnalyses', uid, oldApiCall$);
  }

  getUiTemplate(): Observable<formField[]> {
    return this.apolloClientService.apolloClient$.pipe(
      switchMap(client =>
        client.query<{configuratorTestAnalysisUiTemplate: GraphQLFormFieldOutput[]}>({
          query: GET_TEST_ANALYSIS_UI_TEMPLATE_QUERY,
          variables: {}
        })
      ),
      map(results => {
        const testAnalysisUiTemplate = results.data.configuratorTestAnalysisUiTemplate

        if (!testAnalysisUiTemplate)
          return undefined

        return testAnalysisUiTemplate.map(item => {
          if(item.Name == 'Lnk-TestAnalyze-EeseRequirement')
            item.FieldType = GraphQLFieldTypeEnum.table
          return convertGraphqlFormFieldToDto(item);
        })
      })
    )
  }
  getSearchUiTemplate(): Observable<formField[]> {
    return this.apolloClientService.apolloClient$.pipe(
      switchMap(client =>
        client.query<{configuratorTestAnalysisSearchUiTemplate: GraphQLFormFieldOutput[]}>({
          query: GET_TEST_ANALYSIS_SEARCH_UI_TEMPLATE_QUERY,
          variables: {}
        })
      ),
      map(results => {
        const testAnalysisUiTemplate = results.data.configuratorTestAnalysisSearchUiTemplate

        if (!testAnalysisUiTemplate)
          return undefined

        return testAnalysisUiTemplate.map(item => {
          return convertGraphqlFormFieldToDto(item);
        })
      })
    )
  }

  getAllWithLinkedRequirement(requirementId: string): Observable<BaseTestItemDto[]> {
    return this.apolloClientService.apolloClient$.pipe(
      switchMap(client =>
        client.query<{configuratorItemsWithLink : GraphQLBaseTestItemOutput[]}>({
          query: GET_ITEMS_WITH_LINK_QUERY,
          variables: {ItemTypeId: ItemTypeMapper.mapReverse(ItemType.TestAnalysis), LinkTypeId: 238, LinkValue: requirementId} //238 is link TA-requirement
        })
      ),
      map(results => {
        const testAnalyses = results.data.configuratorItemsWithLink
        if (!testAnalyses)
          return undefined

        return testAnalyses.map(item => {
          return convertGraphqlBaseTestItemToDto(item);
        })
      })
    )
  }

  create(testAnalysis: TestAnalysisDto): Observable<ResponseDto> {
    const graphqlTestAnalysisInput = convertTestAnalysisDtoToGraphqlBaseTestItem(testAnalysis)
    const jsony = JSON.stringify(graphqlTestAnalysisInput);
    const oldApiCall$ = this.apolloClientService.apolloClient$.pipe(
      switchMap(client =>
        client.mutate<{configuratorCreateTestAnalysis: GraphQLResponseOutput}>({
          mutation: CREATE_TEST_ANALYSIS_MUTATION,
          variables: {TestAnalysis: graphqlTestAnalysisInput }
        })
      ),
      map(result => {
        let res = convertGraphqlResponseToDtoSimpleReturnValue(result.data.configuratorCreateTestAnalysis as GraphQLResponseOutput);
        return res;
      })
    );

    return this.dualApiService.dualCreate('testAnalyses', testAnalysis, oldApiCall$);
  }

  update(testAnalysis: TestAnalysisDto): Observable<ResponseDto> {
    const graphqlTestAnalysisInput = convertTestAnalysisDtoToGraphqlBaseTestItem(testAnalysis)

    const oldApiCall$ = this.apolloClientService.apolloClient$.pipe(
      switchMap(client =>
        client.mutate<{configuratorUpdateTestAnalysis: GraphQLResponseOutput}>({
          mutation: UPDATE_TEST_ANALYSIS_MUTATION,
          variables: {TestAnalysis: graphqlTestAnalysisInput }
        })
      ),
      map(result => {
        let res = convertGraphqlResponseToDtoSimpleReturnValue(result.data.configuratorUpdateTestAnalysis as GraphQLResponseOutput);
        return res;
      })
    );

    return this.dualApiService.dualUpdate('testAnalyses', testAnalysis.uid, testAnalysis, oldApiCall$);
  }

  delete(uid: string, regBy: string): Observable<any> {
    const oldApiCall$ = this.apolloClientService.apolloClient$.pipe(
      switchMap(client =>
        client.mutate({
          mutation: DELETE_TEST_ANALYSIS_MUTATION,
          variables: {Uid: uid}
        })
      ),
      map(result =>(result.data))
    );

    return this.dualApiService.dualDelete('testAnalyses', uid, oldApiCall$);
  }

  search(testAnalysis: TestAnalysisDto, scope?: Scope): Observable<BaseTestItemDto[]> {
    const graphqlTestAnalysisInput = convertTestAnalysisDtoToGraphqlBaseTestItem(testAnalysis);

    const oldApiCall$ = this.apolloClientService.apolloClient$.pipe(
      switchMap(client =>
        client.query<{configuratorSearchTestAnalysis: GraphQLBaseTestItemOutput[]}>({
          query: SEARCH_TEST_ANALYSIS_QUERY,
          variables: {TestAnalysis: graphqlTestAnalysisInput }
        })
      ),
      map(results => {
        const testAnalyses = results?.data?.configuratorSearchTestAnalysis
        if (!testAnalyses)
          return undefined;

        return testAnalyses.map(item => {
          return convertGraphqlBaseTestItemToDto(item);
        })
      })
    );

    return this.dualApiService.dualSearch('testAnalyses', testAnalysis, oldApiCall$, scope);
  }
}
